import React, { useState } from "react";
import { Box, Button, Checkbox, FormControlLabel, IconButton, InputAdornment, Tooltip, Typography, Paper, Divider } from "@mui/material";
import { InfoOutlined, MailOutline, LockOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import styles from "./styles.module.scss";
import { NotistackServices, UserServices } from "shared/services";
import Icon from "@mdi/react";
import { mdiAccountCircle } from "@mdi/js";
import { Link, OTPInput } from "components";
import { CustomTextField } from "components";
import { useNavigate } from "react-router-dom";
import { HandleError } from "shared/utils";

export const LoginPage = () => {
	const navigate = useNavigate();
	const [stage, setStage] = useState<"login" | "2fa" | "pin">("login");
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [account, setAccount] = React.useState("");
	const [password, setPassword] = React.useState("");
	const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
	const [pin, setPin] = useState<string[]>(new Array(4).fill(""));
	const [rememberMe, setRememberMe] = useState(false);

	const handleClickShowPassword = () => setShowPassword((show) => !show);
	const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault();
	};

	const handleSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		setLoading(true);

		try {
			await UserServices.signInWithAccountAndPassword(
				account,
				password,
				stage === "2fa" ? otp.join("") : undefined,
				rememberMe,
				stage === "pin" ? pin.join("") : undefined,
			);
			navigate("/", { replace: true });
		} catch (error) {
			if (HandleError.isHandleError(error)) {
				switch (error.name) {
					case "USER_NOT_FOUND":
					case "INVALID_PASSWORD":
						NotistackServices.showErrorMessage("Credenciais inválidas. Verifique seu email/usuário e senha e tente novamente.");
						setLoading(false);
						return;
					case "2FA_REQUIRED":
						setStage("2fa");
						setLoading(false);
						return;
					case "PIN_REQUIRED":
						setStage("pin");
						setLoading(false);
						return;
					default:
						break;
				}
			}

			NotistackServices.showErrorMessage(`Falha ao entrar. Verifique suas credenciais e tente novamente. Erro: ${(error as Error).message}!`);
			setLoading(false);
		}
	};

	const handleByProvider = (provider: string) => () => {
		NotistackServices.showInfoMessage(`Login via ${provider} ainda não implementado.`);
	};

	return (
		<Paper
			elevation={0}
			className={styles.card}
		>
			{stage === "login" ? (
				<>
					<form
						noValidate
						onSubmit={handleSubmit}
					>
						<CustomTextField
							id="email"
							label="Email ou Usuário"
							placeholder="exemplo@empresa.com"
							value={account}
							onChange={(e) => setAccount(e.target.value)}
							disabled={loading}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<MailOutline sx={{ color: "currentColor" }} />
									</InputAdornment>
								),
							}}
						/>

						<CustomTextField
							id="password"
							placeholder="••••••••"
							type={showPassword ? "text" : "password"}
							value={password}
							onChange={(e) => setPassword(e.target.value)}
							disabled={loading}
							renderLabelHeader={() => (
								<>
									<label
										htmlFor="password"
										className={styles.label}
										style={{ marginBottom: 0 }}
									>
										Senha
									</label>
									<Link
										href="./forgot-password"
										className={styles.forgotLink}
										preserveQueryParams
										disabled={loading}
									>
										Esqueci minha senha
									</Link>
								</>
							)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<LockOutlined sx={{ color: "currentColor" }} />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											aria-label="toggle password visibility"
											onClick={handleClickShowPassword}
											onMouseDown={handleMouseDownPassword}
											edge="end"
											disabled={loading}
											sx={{ "color": "currentColor", "&:hover": { color: "text.primary" } }}
										>
											{showPassword ? <VisibilityOff /> : <Visibility />}
										</IconButton>
									</InputAdornment>
								),
							}}
						/>

						<Box sx={{ display: "flex", alignItems: "center", mt: 1, mb: 0.5 }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={rememberMe}
										onChange={(e) => setRememberMe(e.target.checked)}
										disabled={loading}
										size="small"
										sx={{
											"color": "rgba(var(--mui-palette-text-primaryChannel) / 0.5)",
											"&.Mui-checked": { color: "var(--mui-palette-primary-main)" },
										}}
									/>
								}
								label={
									<Typography
										variant="body2"
										sx={{ color: "rgba(var(--mui-palette-text-primaryChannel) / 0.75)", fontSize: "0.8rem" }}
									>
										Manter-me conectado neste dispositivo
									</Typography>
								}
								sx={{ mr: 0.5 }}
							/>
							<Tooltip
								title="Ao selecionar esta opção, sua sessão permanecerá ativa por 30 dias. Não recomendado em dispositivos compartilhados."
								arrow
								placement="top"
							>
								<InfoOutlined
									sx={{ fontSize: "1rem", color: "rgba(var(--mui-palette-text-primaryChannel) / 0.4)", cursor: "help" }}
								/>
							</Tooltip>
						</Box>

						<Button
							fullWidth
							variant="contained"
							size="large"
							className={styles.submitButton}
							disabled={loading}
							type="submit"
						>
							{loading ? "Entrando..." : "Entrar na Plataforma"}
						</Button>
					</form>

					<div className={styles.dividerContainer}>
						<Divider className={styles.dividerLine}>
							<Typography
								variant="caption"
								className={styles.dividerText}
							>
								Ou continue com
							</Typography>
						</Divider>
					</div>

					<Box className={styles.socialButtonsGrid}>
						<Button
							fullWidth
							variant="outlined"
							className={styles.socialButton}
							onClick={handleByProvider("google")}
							disabled={loading}
						>
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
									fill="#4285F4"
								/>
								<path
									d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
									fill="#34A853"
								/>
								<path
									d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
									fill="#FBBC05"
								/>
								<path
									d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
									fill="#EA4335"
								/>
							</svg>
							Entrar com Google
						</Button>

						{/* <Button
							fullWidth
							variant="outlined"
							className={styles.socialButton}
						>
							<svg
								width="20"
								height="20"
								viewBox="0 0 23 23"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M0 0h11v11H0zM12 0h11v11H12zM0 12h11v11H0zM12 12h11v11H12z"
									fill="#f3f3f3"
								/>
							</svg>
							Microsoft Azure
						</Button> */}
						<Link
							href="./create-account"
							className={styles.forgotLink}
							underline="none"
							preserveQueryParams
							disabled={loading}
						>
							<Button
								fullWidth
								variant="outlined"
								className={styles.socialButton}
								onClick={(e) => {
									e.preventDefault();
								}}
								disabled={loading}
							>
								<Icon
									path={mdiAccountCircle}
									size={1}
								/>
								Criar Conta
							</Button>
						</Link>
					</Box>
				</>
			) : stage === "2fa" ? (
				<form
					noValidate
					onSubmit={handleSubmit}
					style={{ textAlign: "center" }}
				>
					<Typography
						variant="h5"
						sx={{ mb: 1, fontWeight: 600 }}
					>
						Verificação em duas etapas
					</Typography>
					<Typography
						variant="body1"
						sx={{ mb: 4, opacity: 0.8 }}
					>
						Digite o código gerado pelo seu aplicativo autenticador para continuar.
					</Typography>

					<Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
						<OTPInput
							value={otp}
							onChange={setOtp}
							disabled={loading}
						/>
					</Box>

					<Button
						fullWidth
						variant="contained"
						size="large"
						className={styles.submitButton}
						disabled={loading}
						type="submit"
					>
						{loading ? "Verificando..." : "Verificar"}
					</Button>

					<Button
						variant="text"
						size="small"
						onClick={() => setStage("login")}
						disabled={loading}
						sx={{ mt: 2, color: "text.primary", textTransform: "none" }}
					>
						Voltar para o login
					</Button>
				</form>
			) : (
				<form
					noValidate
					onSubmit={handleSubmit}
					style={{ textAlign: "center" }}
				>
					<Typography
						variant="h5"
						sx={{ mb: 1, fontWeight: 600 }}
					>
						PIN de Segurança
					</Typography>
					<Typography
						variant="body1"
						sx={{ mb: 4, opacity: 0.8 }}
					>
						Digite seu PIN de 4 dígitos para continuar.
					</Typography>

					<Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
						<OTPInput
							value={pin}
							onChange={setPin}
							disabled={loading}
						/>
					</Box>

					<Button
						fullWidth
						variant="contained"
						size="large"
						className={styles.submitButton}
						disabled={loading || pin.join("").length !== 4}
						type="submit"
					>
						{loading ? "Verificando..." : "Verificar PIN"}
					</Button>

					<Button
						variant="text"
						size="small"
						onClick={() => setStage("login")}
						disabled={loading}
						sx={{ mt: 2, color: "text.primary", textTransform: "none" }}
					>
						Voltar para o login
					</Button>
				</form>
			)}
		</Paper>
	);
};
